import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import {
  MenuItem, Select, InputLabel, FormControl, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Button as MaterialButton,
} from '@material-ui/core';
import { Delete, Edit } from '@material-ui/icons';
import api from 'api';
import { Button } from 'components/common/Button/Button';
import { MyPageTable } from 'components/common/myPageTable/MyPageTable';
import { selectSelectedSupplierId } from 'store/suppliersList/Selector';
import { getSuppliersList } from 'store/suppliersList/ActionCreator';
import { startLoading, finishLoading } from 'hooks/useProgress';
import { SupplierId } from 'store/suppliersList/types';
import { AppState } from 'store/RootReducer';
import { showSnackbar } from 'hooks/useSnackbar';
import cn from './ScoreTab.module.scss';

type MapStateToProps = {
  selectedSupplierId: SupplierId;
};

type MapDispatchToProps = {
  refreshSuppliers: () => void;
};

type ScoreTabProps = MapStateToProps & MapDispatchToProps;

type Score = {
  id: number;
  supplierCardId: string;
  date: string;
  systemBusinessAreaId: number;
  capacity: number;
  communication: number;
  price: number;
  claim: number;
};

const ratingOptions = [1, 2, 3, 4, 5];

const ScoreTabComponent = React.memo(({ selectedSupplierId, refreshSuppliers }: ScoreTabProps) => {
  const [scores, setScores] = useState<Score[]>([]);
  const [businessAreas, setBusinessAreas] = useState<Array<{ id: number; name: string }>>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingScore, setEditingScore] = useState<Score | null>(null);
  const [capacity, setCapacity] = useState(3);
  const [communication, setCommunication] = useState(3);
  const [price, setPrice] = useState(3);
  const [claim, setClaim] = useState(3);
  const [businessAreaId, setBusinessAreaId] = useState(0);

  useEffect(() => {
    api.getBusinessAreas().then(areas => {
      setBusinessAreas(areas);
      if (areas.length > 0) setBusinessAreaId(areas[0].id);
    }).catch(() => {});
  }, []);

  const requestData = useCallback(async () => {
    startLoading();
    try {
      const data = await api.getScores(selectedSupplierId);
      setScores(data);
    } catch (err) {}
    finishLoading();
  }, [selectedSupplierId]);

  useEffect(() => {
    if (selectedSupplierId) {
      requestData();
    }
  }, [selectedSupplierId, requestData]);

  function openAddModal(): void {
    setEditingScore(null);
    setCapacity(3);
    setCommunication(3);
    setPrice(3);
    setClaim(3);
    if (businessAreas.length > 0) setBusinessAreaId(businessAreas[0].id);
    setModalOpen(true);
  }

  function openEditModal(score: Score): void {
    setEditingScore(score);
    setCapacity(score.capacity);
    setCommunication(score.communication);
    setPrice(score.price);
    setClaim(score.claim);
    setBusinessAreaId(score.systemBusinessAreaId);
    setModalOpen(true);
  }

  async function handleSave(): Promise<void> {
    startLoading();
    try {
      if (editingScore) {
        await api.deleteScore(selectedSupplierId, editingScore.id);
      }
      await api.addScore(selectedSupplierId, {
        systemBusinessAreaId: businessAreaId,
        capacity,
        communication,
        price,
        claim,
      });
      showSnackbar({ message: editingScore ? 'Assessment updated.' : 'Assessment added.', type: 'success' });
      setModalOpen(false);
      requestData();
      refreshSuppliers();
    } catch (err) {}
    finishLoading();
  }

  async function handleDelete(scoreId: number): Promise<void> {
    startLoading();
    try {
      await api.deleteScore(selectedSupplierId, scoreId);
      showSnackbar({ message: 'Assessment deleted.', type: 'success' });
      requestData();
      refreshSuppliers();
    } catch (err) {}
    finishLoading();
  }

  const tableData = scores.map(s => ({
    ...s,
    date: s.date ? new Date(s.date).toISOString().split('T')[0] : '',
    businessArea: businessAreas.find(a => a.id === s.systemBusinessAreaId)?.name || s.systemBusinessAreaId,
    actions: (
      <>
        <IconButton size="small" onClick={() => openEditModal(s)} title="Edit">
          <Edit fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => handleDelete(s.id)} title="Delete">
          <Delete fontSize="small" />
        </IconButton>
      </>
    ),
  }));

  return (
    <div className={cn.scoreContainer}>
      <Button onClick={openAddModal} className={cn.addButton}>
        Add Assessment
      </Button>
      <MyPageTable
        tableData={tableData}
        padding="small"
        disableTooltip={true}
        columns={[
          { name: 'date', title: 'Date' },
          { name: 'businessArea', title: 'Business Area' },
          { name: 'capacity', title: 'Capacity' },
          { name: 'communication', title: 'Communication' },
          { name: 'price', title: 'Price' },
          { name: 'claim', title: 'Claim' },
          { name: 'actions', title: 'Actions' },
        ]}
        defaultSorting={[{ columnName: 'date', direction: 'desc' }]}
      />
      {scores.length > 0 && (
        <div className={cn.trendSection}>
          <div className={cn.trendTitle}>12-Month Trend</div>
          <div className={cn.trendChart}>
            {(() => {
              const now = new Date();
              const months: { label: string; avg: number }[] = [];
              for (let i = 11; i >= 0; i--) {
                const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                const label = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                const monthScores = scores.filter(s => {
                  const sd = new Date(s.date);
                  return sd.getMonth() === d.getMonth() && sd.getFullYear() === d.getFullYear();
                });
                const avg = monthScores.length > 0
                  ? monthScores.reduce((sum: number, s: Score) => sum + s.capacity + s.communication + s.price + s.claim, 0) / (monthScores.length * 4)
                  : 0;
                months.push({ label, avg });
              }
              return months.map((m, i) => (
                <div key={i} className={cn.trendBar}>
                  <div className={cn.trendBarFill} style={{ height: `${(m.avg / 5) * 100}%` }} title={m.avg.toFixed(1)} />
                  <div className={cn.trendBarLabel}>{m.label}</div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingScore ? 'Edit Assessment' : 'Add Assessment'}</DialogTitle>
        <DialogContent>
          <div className={cn.modalForm}>
            <FormControl variant="outlined" size="small" fullWidth className={cn.modalField}>
              <InputLabel>Business Area</InputLabel>
              <Select value={businessAreaId} onChange={(e) => setBusinessAreaId(e.target.value as number)} label="Business Area">
                {businessAreas.map(a => (
                  <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {[
              { label: 'Capacity', value: capacity, setter: setCapacity },
              { label: 'Communication', value: communication, setter: setCommunication },
              { label: 'Price', value: price, setter: setPrice },
              { label: 'Claim', value: claim, setter: setClaim },
            ].map(f => (
              <FormControl key={f.label} variant="outlined" size="small" fullWidth className={cn.modalField}>
                <InputLabel>{f.label}</InputLabel>
                <Select value={f.value} onChange={(e) => f.setter(e.target.value as number)} label={f.label}>
                  {ratingOptions.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <MaterialButton onClick={handleSave} color="primary">
            {editingScore ? 'Update' : 'Add'}
          </MaterialButton>
          <MaterialButton onClick={() => setModalOpen(false)} color="primary">
            Cancel
          </MaterialButton>
        </DialogActions>
      </Dialog>
    </div>
  );
});

const mapStateToProps = (state: AppState): MapStateToProps => ({
  selectedSupplierId: selectSelectedSupplierId(state),
});

const mapDispatchToProps: MapDispatchToProps = {
  refreshSuppliers: getSuppliersList,
};

export const ScoreTab = connect(mapStateToProps, mapDispatchToProps)(ScoreTabComponent);
